import PhonePicker from '@/components/PhonePicker';
import GuestSuggestions from '@/features/reservations/components/GuestSuggestions';
import {
  EMAIL_MAX,
  NAME_MAX,
  isEmailInvalid,
  primaryPhone,
  textInput,
  type CreateValues,
} from '@/features/reservations/model/reservation-form';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { formatPhone, isValidGuestPhone } from '@/utils/phone';
import type { Customer } from '@restaurant/api-client';
import { ReservationSource } from '@restaurant/types';
import { BlockTitle, Link as F7Link, List, ListInput, ListItem, f7 } from 'framework7-react';
import { useController, useWatch, type Control } from 'react-hook-form';

interface Props {
  control: Control<CreateValues, unknown, unknown>;
  onPhoneValid: (valid: boolean) => void;
}

export default function CreateGuestSection({ control, onPhoneValid }: Props) {
  const tr = useT();
  // Restaurant country drives phone parsing so local-format numbers validate.
  const country = useRestaurantStore((s) => s.restaurantConfig?.country ?? undefined);
  const isIos = f7.theme === 'ios';
  const { field: name } = useController({ control, name: 'name' });
  const { field: email } = useController({ control, name: 'email' });
  const { field: phone } = useController({ control, name: 'phone' });
  // One unified guest flow: the operator just types the guest's name or phone.
  // Matching franchise customers surface in a dropdown - tap to reuse (so we
  // never create a duplicate), or ignore it and the typed details become a new
  // guest. No upfront "new vs existing" choice: the operator rarely knows which
  // until the search answers it. Picking a guest collapses the inputs into a
  // locked summary card; "Change" reopens the fields.
  const { field: customerId } = useController({ control, name: 'customerId' });
  const source = useWatch({ control, name: 'source' });

  const clearGuest = () => {
    name.onChange('');
    email.onChange('');
    phone.onChange('');
    onPhoneValid(false);
    customerId.onChange(null);
  };

  const pickCustomer = (c: Customer) => {
    const picked = primaryPhone(c);
    name.onChange(`${c.firstName} ${c.lastName}`.trim());
    email.onChange(c.email ?? '');
    phone.onChange(picked);
    // A returning customer's stored number is canonical E.164 - trust it, but
    // still verify so a legacy bad record can't slip a phone booking through.
    onPhoneValid(!!picked && isValidGuestPhone(picked, country));
    customerId.onChange(c.id);
  };

  return (
    <>
      <BlockTitle>{tr.editReservation.sectionGuest}</BlockTitle>
      {customerId.value ? (
        <List strongIos insetIos dividersIos>
          <ListItem
            title={name.value}
            footer={
              [formatPhone(phone.value) || phone.value, email.value].filter(Boolean).join(' · ') ||
              undefined
            }
          >
            <i
              slot="media"
              className="f7-icons"
              style={{ fontSize: 26, color: 'var(--f7-theme-color)' }}
            >
              person_crop_circle_fill
            </i>
            <F7Link slot="after" onClick={clearGuest}>
              {tr.createReservation.change}
            </F7Link>
          </ListItem>
        </List>
      ) : (
        <List strongIos insetIos dividersIos>
          <ListInput
            label={tr.createReservation.fullName}
            placeholder={tr.createReservation.searchExistingPlaceholder}
            maxlength={NAME_MAX}
            outline={!isIos}
            floatingLabel={!isIos}
            clearButton={isIos}
            {...textInput(name)}
          />
          <li>
            <PhonePicker
              label={
                source === ReservationSource.PHONE
                  ? tr.reservations.detail.phone
                  : `${tr.reservations.detail.phone} ${tr.createReservation.optional}`
              }
              value={phone.value}
              defaultCountry={country}
              placeholder="170 1234567"
              onChange={(e164, valid) => {
                phone.onChange(e164);
                onPhoneValid(valid);
              }}
            />
          </li>
          <ListInput
            label={`${tr.reservations.detail.email} ${tr.createReservation.optional}`}
            type="email"
            inputmode="email"
            maxlength={EMAIL_MAX}
            placeholder="john@example.com"
            outline={!isIos}
            floatingLabel={!isIos}
            clearButton={isIos}
            errorMessage={tr.createReservation.emailInvalid}
            errorMessageForce={isEmailInvalid(email.value)}
            {...textInput(email)}
          />
        </List>
      )}

      <GuestSuggestions
        name={name.value}
        phone={phone.value}
        picked={!!customerId.value}
        onPick={pickCustomer}
      />
    </>
  );
}
